import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
//Context
import { useHome } from './HomeContext';
//Styles
import '../../styles/home.css';
import Chart from 'react-apexcharts';
import ApexCharts from 'apexcharts';

class ChartErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            const info = this.props.debugInfo || {};
            return (
                <div className="noDataPlaceholder">
                    Error al renderizar el gráfico.
                    <div>{info.label}</div>
                    <div>{info.details}</div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default function GanttGraph({ lineId, series, statusColor, lineName }) {
    const {
        lines,
        lineSelect,
        nSeries,
        estatusLinea,
        seriesByLine,
        statusByLine,
        fechaWIni,
        fechaWFin,
    } = useHome();
    const es = require('apexcharts/dist/locales/es.json');
    const [flag, setFlag] = useState(false);
    const chartId = `rangeBar-${lineId ?? lineSelect ?? 'single'}`;
    const hoveredRef = useRef({ indices: [], pointIndex: 0 });
    const applyingRangeRef = useRef(false);
    const resetSelection = useCallback(
        (chartContext) => {
            const toggle =
                chartContext && typeof chartContext.toggleDataPointSelection === 'function'
                    ? chartContext.toggleDataPointSelection.bind(chartContext)
                    : null;
            ApexCharts.exec(chartId, 'clearSelection');
            if (chartContext && typeof chartContext.clearSelection === 'function') {
                chartContext.clearSelection();
            }
            const selected = chartContext?.w?.globals?.selectedDataPoints;
            if (toggle && Array.isArray(selected)) {
                selected.forEach((seriesPoints, seriesIdx) => {
                    if (!Array.isArray(seriesPoints)) {
                        return;
                    }
                    seriesPoints.forEach((pointIdx) => {
                        if (typeof pointIdx === 'number') {
                            toggle(seriesIdx, pointIdx);
                        }
                    });
                });
                if (chartContext?.w?.globals) {
                    chartContext.w.globals.selectedDataPoints = [];
                }
            }
            const el = chartContext?.el;
            if (el?.querySelectorAll) {
                el.querySelectorAll('.apexcharts-selected').forEach((node) => {
                    node.classList.remove('apexcharts-selected');
                });
            }
            hoveredRef.current = { indices: [], pointIndex: 0 };
        },
        [chartId]
    );

    const resolvedSeries = useMemo(() => {
        if (series) {
            return series;
        }
        if (lineId !== undefined && lineId !== null) {
            return seriesByLine?.[lineId] ?? [];
        }
        return nSeries ?? [];
    }, [series, seriesByLine, lineId, nSeries]);
    const safeSeries = useMemo(() => {
        return resolvedSeries
            .map((serie) => {
                if (!Array.isArray(serie.data)) {
                    return null;
                }
                const data = serie.data.filter(
                    (point) =>
                        Array.isArray(point.y) &&
                        point.y.length === 2 &&
                        Number.isFinite(point.y[0]) &&
                        Number.isFinite(point.y[1])
                );
                if (data.length === 0) {
                    return null;
                }
                return { ...serie, data };
            })
            .filter(Boolean);
    }, [resolvedSeries]);
    const hasSeries = safeSeries.length > 0;
    const resolvedStatus =
        statusColor ??
        (lineId !== undefined && lineId !== null ? statusByLine?.[lineId] : estatusLinea) ??
        '';
    const selectedLine = lines.find(
        (line) => line.id === (lineId !== undefined && lineId !== null ? lineId : lineSelect)
    );
    const headerName = lineName ?? (selectedLine ? selectedLine.nombre : '');
    const formatDate = useCallback(
        (value) =>
            new Date(value).toLocaleString().slice(0, new Date(value).toLocaleString().length - 3),
        []
    );

    const xaxisRange = useMemo(() => {
        let dataMin = Infinity;
        let dataMax = -Infinity;
        safeSeries.forEach((serie) => {
            (serie.data || []).forEach((point) => {
                if (Array.isArray(point.y) && point.y.length >= 2) {
                    if (Number.isFinite(point.y[0])) dataMin = Math.min(dataMin, point.y[0]);
                    if (Number.isFinite(point.y[1])) dataMax = Math.max(dataMax, point.y[1]);
                }
            });
        });
        const rangeStart = fechaWIni ? new Date(new Date(fechaWIni).setHours(0, 0, 0, 0)) : null;
        const rangeEnd = fechaWFin ? new Date(new Date(fechaWFin).setHours(23, 59, 59, 999)) : null;
        const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
        let min = rangeStart ? rangeStart.getTime() : dataMin;
        let max = rangeEnd ? rangeEnd.getTime() : dataMax;
        if (Number.isFinite(dataMin) && Number.isFinite(dataMax)) {
            min = Math.min(min, dataMin);
            max = Math.max(max, dataMax);
        }
        if (Number.isFinite(min) && Number.isFinite(max) && max - min < threeDaysMs) {
            const center = (min + max) / 2;
            min = center - threeDaysMs / 2;
            max = center + threeDaysMs / 2;
        }
        const dataMinFinite = Number.isFinite(dataMin) ? dataMin : null;
        const dataMaxFinite = Number.isFinite(dataMax) ? dataMax : null;
        return {
            min: Number.isFinite(min) ? min : undefined,
            max: Number.isFinite(max) ? max : undefined,
            dataMin: dataMinFinite,
            dataMax: dataMaxFinite,
        };
    }, [safeSeries, fechaWIni, fechaWFin]);

    const options = useMemo(
        () => ({
            annotations: {
                position: 'front',
                xaxis: [
                    {
                        x: new Date().getTime(),
                        x2: null,

                        strokeDashArray: 0,
                        // borderColor: '#5E3F94',
                        // fillColor: '#5E3F94',
                        opacity: 1,
                        offsetX: 0,
                        offsetY: 0,
                        label: {
                            // borderColor: '#5E3F94',
                            borderWidth: 1,
                            borderRadius: 2,
                            textAnchor: 'middle',
                            position: 'top',
                            orientation: 'vertical',
                        },
                    },
                ],
            },
            // colors: ['#5E3F94'],

            dataLabels: {
                enabled: true,
                formatter: function (val, opts) {
                    const seriesName = opts.w.config.series?.[opts.seriesIndex]?.name;
                    return seriesName ?? '';
                },
                distributed: false,
                style: {
                    colors: ['#fff'],
                    fontSize: '14px',
                    fontFamily: 'WorkSans',
                    fontWeight: 600,
                },
            },
            chart: {
                redrawOnWindowResize: true,
                redrawOnParentResize: true,
                type: 'rangeBar',
                group: undefined,
                id: chartId,
                responsive: [
                    {
                        breakpoint: 200,
                        options: {
                            chart: {
                                width: '40%',
                            },
                        },
                    },
                ],
                events: {
                    dataPointMouseEnter: function (event, chartContext, config) {
                        resetSelection(chartContext);
                        const toggle =
                            chartContext &&
                            typeof chartContext.toggleDataPointSelection === 'function'
                                ? chartContext.toggleDataPointSelection.bind(chartContext)
                                : null;
                        const series = config?.w?.config?.series || [];
                        const name = series?.[config.seriesIndex]?.name;
                        if (!toggle || !name) {
                            return;
                        }
                        const pointIndex =
                            typeof config.dataPointIndex === 'number' ? config.dataPointIndex : 0;
                        const matches = series
                            .map((serie, idx) => (serie?.name === name ? idx : -1))
                            .filter((idx) => idx !== -1);
                        matches.forEach((idx) => toggle(idx, pointIndex));
                        hoveredRef.current = { indices: matches, pointIndex };
                    },
                    dataPointMouseLeave: function (event, chartContext) {
                        resetSelection(chartContext);
                    },
                    mouseLeave: function (event, chartContext) {
                        resetSelection(chartContext);
                    },
                    updated: function (chartContext) {
                        if (applyingRangeRef.current) {
                            applyingRangeRef.current = false;
                            return;
                        }
                        const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
                        const globals = chartContext?.w?.globals;
                        if (!globals || xaxisRange.min == null || xaxisRange.max == null) return;
                        const minX = globals.minX;
                        const maxX = globals.maxX;
                        if (
                            typeof minX !== 'number' ||
                            typeof maxX !== 'number' ||
                            maxX - minX >= threeDaysMs
                        ) {
                            return;
                        }
                        const { dataMin, dataMax } = xaxisRange;
                        const tolerance = 60 * 1000;
                        const isResetToData =
                            dataMin != null &&
                            dataMax != null &&
                            Math.abs(minX - dataMin) <= tolerance &&
                            Math.abs(maxX - dataMax) <= tolerance;
                        if (isResetToData) {
                            applyingRangeRef.current = true;
                            ApexCharts.exec(
                                chartId,
                                'updateOptions',
                                { xaxis: { min: xaxisRange.min, max: xaxisRange.max } },
                                false,
                                false
                            );
                        }
                    },
                },
                animations: {
                    enabled: false,
                },
                locales: [es],
                defaultLocale: 'es',
                fontFamily: 'WorkSans',
                offsetY: -80,
                toolbar: {
                    show: true,
                    offsetY: -120,
                    tools: {
                        download: false,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                    },
                    autoSelected: 'pan',
                },
                zoom: {
                    enabled: true,
                },
            },
            states: {
                hover: {
                    filter: {
                        type: 'lighten',
                        value: 0.15,
                    },
                },
                active: {
                    allowMultipleDataPointsSelection: false,
                    filter: {
                        type: 'lighten',
                        value: 0.15,
                    },
                },
            },

            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: 60,
                    borderRadius: 5,
                    borderRadiusApplication: 'around',
                    // borderRadiusWhenStacked: 'all',
                    distributed: true,
                    rangeBarGroupRows: true,
                    // rangeBarOverlap: false,
                    dataLabels: {
                        show: true,
                        position: 'center',
                        orientation: 'horizontal',
                        hideOverflowingLabels: true,
                    },
                },
            },
            xaxis: {
                type: 'datetime',
                position: 'Top',
                tickAmount: 8,
                min: xaxisRange.min,
                max: xaxisRange.max,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: "MMM 'yy",
                    day: 'dd MMM',
                    hour: 'HH',
                },
                axisBorder: {
                    show: true,
                    color: '#78909C',
                    height: 1,
                    width: '100%',
                    offsetX: 0,
                    offsetY: 0,
                },
                labels: {
                    show: true,
                    offsetY: -16,
                    rotate: 0,
                    rotateAlways: false,
                    hideOverlappingLabels: true,
                    showDuplicates: false,
                    maxHeight: 80,
                    datetimeUTC: false,
                    datetimeFormatter: {
                        year: 'yyyy',
                        month: "MMM 'yy",
                        day: 'dd MMM',
                        hour: 'HH:mm',
                    },
                    style: {
                        fontSize: '14px',
                        fontFamily: 'WorkSans',
                        fontWeight: 600,
                        cssClass: 'apexcharts-xaxis-label',
                        textAlign: 'left',
                        letterSpacing: 0,
                        color: '#2B2930',
                        opacity: 1,
                    },
                },
            },
            yaxis: {
                show: true,
                axisBorder: {
                    show: true,
                    color: '#78909C',
                    offsetX: 0,
                    offsetY: 0,
                },
                labels: {
                    show: true,
                    minWidth: 40,
                    align: 'right',
                    style: {
                        fontSize: '18px',
                        fontFamily: 'WorkSans',
                        fontWeight: 600,
                        cssClass: 'apexcharts-yaxis-label',
                    },
                },
            },

            stroke: {
                width: 1,
            },
            fill: {
                type: 'solid',
                opacity: 0.8,
            },
            legend: {
                show: false,
                position: 'bottom',
                onItemClick: {
                    toggleDataSeries: true,
                },
            },

            grid: {
                show: true,
                borderColor: '#90A4AE',
                strokeDashArray: 0,
                position: 'front',
                xaxis: {
                    lines: {
                        show: false,
                    },
                },
                yaxis: {
                    lines: {
                        show: true,
                    },
                },
                row: {
                    colors: undefined,
                    opacity: 0.5,
                },
                column: {
                    colors: undefined,
                    opacity: 0.5,
                },
                padding: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
            },
            tooltip: {
                enabled: true,
                enabledOnSeries: undefined,
                shared: false,
                followCursor: true,
                fillSeriesColor: true,
                offsetX: 0,
                offsetY: 0,
                fixed: {
                    enabled: false,
                },

                // x: {
                //     show: true,
                //     format: 'dd MMM hh:mm',
                // },

                custom: function ({ seriesIndex, dataPointIndex, w }) {
                    const dataPoint = w.config.series?.[seriesIndex]?.data?.[dataPointIndex];
                    const serieMeta = safeSeries?.[seriesIndex];
                    if (!dataPoint?.y) {
                        return '';
                    }
                    const descripcion = (dataPoint.z?.[1] ?? '').toString();
                    const isMantenimiento = descripcion.toLowerCase().includes('mantenimiento');
                    const fechaIni = formatDate(dataPoint.y[0]);
                    const fechaFin = formatDate(dataPoint.y[1]);
                    if (dataPoint.x === 'Plan') {
                        if (dataPoint.z?.[0] !== 'NA') {
                            const ordenValue =
                                serieMeta?.orden ?? w.config.series[seriesIndex].name ?? '';
                            const titleText = isMantenimiento
                                ? 'Mantenimiento'
                                : `Orden: ${ordenValue}`;
                            const metaLine = isMantenimiento
                                ? ''
                                : `Meta: ${dataPoint.z?.[0]}</br>`;
                            const skuLine = isMantenimiento
                                ? ''
                                : `Nombre Sku: ${dataPoint.z?.[1]}<br/>`;
                            const corridaLine = isMantenimiento
                                ? ''
                                : `Corrida: ${w.config.series[seriesIndex].name}`;
                            return `
                            <div class="arrow_box">
                                <h3>${titleText}</h3>
                                <span>
                                    <b>${dataPoint.x}: </b></br>
                                    Fecha inicial: ${fechaIni}</br>
                                    Fecha Final: ${fechaFin}</br>
                                    ${metaLine}
                                    ${skuLine}
                                    ${corridaLine}
                                </span>
                            </div>
                        `;
                        } else {
                            const titleText = isMantenimiento ? 'Mantenimiento' : 'Paro Planeado';
                            return `
                            <div class="arrow_box">
                                <h3>${titleText}</h3>
                                <span>
                                    <b>${dataPoint.x}: </b></br>
                                    Fecha inicial: ${fechaIni}</br>
                                    Fecha Final: ${fechaFin}</br>
                                    Motivo: ${dataPoint.z?.[1]}
                                </span>
                            </div>
                        `;
                        }
                    } else {
                        const ordenValue =
                            serieMeta?.orden ?? w.config.series[seriesIndex].name ?? '';
                        const titleText = isMantenimiento
                            ? 'Mantenimiento'
                            : `Orden: ${ordenValue}`;
                        const skuLine = isMantenimiento
                            ? ''
                            : `Nombre Sku: ${dataPoint.z?.[1]}</br>`;
                        const corridaLine = isMantenimiento
                            ? ''
                            : `Corrida: ${serieMeta?.corrida ?? w.config.series[seriesIndex].name}`;
                        return `
                        <div class="arrow_box">
                            <h3>${titleText}</h3>
                            <span>
                                <b>${dataPoint.x}: </b></br>
                                Fecha inicial: ${fechaIni}</br>
                                Fecha Final: ${fechaFin}</br>
                                Producido: ${dataPoint.z?.[0]}</br>
                                ${skuLine}
                                ${corridaLine}
                            </span>
                        </div>
                    `;
                    }
                },
            },

            noData: {
                text: 'No existen corridas para la fecha seleccionada',
                align: 'center',
                verticalAlign: 'middle',
                offsetX: 0,
                offsetY: 0,
                style: {
                    color: undefined,
                    fontSize: '25px',
                    fontFamily: undefined,
                },
            },
        }),
        [chartId, es, formatDate, resetSelection, safeSeries, xaxisRange]
    );

    useEffect(() => {
        if (lines.length > 0) {
            setFlag(true);
        }
    }, [lines]);

    // El color del estado es arbitrario (viene del backend), así que se aplica como
    // CSS var por ref en vez de style inline (prohibido por react/forbid-dom-props).
    const statusBarRef = useCallback(
        (node) => {
            if (node) node.style.setProperty('--gantt-status', resolvedStatus);
        },
        [resolvedStatus]
    );

    return (
        <div className="wrapper">
            {flag === true ? (
                <div className="line">
                    <div className="gantt-status-bar" ref={statusBarRef} />
                    <div className="gantt-status-label">
                        <h3>{headerName} </h3>
                    </div>
                </div>
            ) : null}

            <div className="graphPvR">
                <div id="Plan">
                    {hasSeries ? (
                        <ChartErrorBoundary
                            debugInfo={{
                                label: `chartId: ${chartId}`,
                                details: `series: ${safeSeries.length}`,
                            }}
                        >
                            <Chart
                                options={options}
                                series={safeSeries}
                                type="rangeBar"
                                height={180}
                                width="100%"
                            />
                        </ChartErrorBoundary>
                    ) : (
                        <div className="noDataPlaceholder">
                            No existen corridas para la fecha seleccionada
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
